import {
	GetCollectionItemsIndexResponse,
	GetCollectionsShowResponse,
	GetScreensShowResponse,
	Manifest,
	MediaItem,
} from 'mytours-read-api-typescript'
import { ManifestFull, ManifestFullItem } from 'stqry-bunny-read-api'

import { DexieDownload, DownloadState } from '../db/types'
import {
	DownloadEntry,
	ExpandContext,
	expandCollection,
	expandCollectionDocument,
	expandItems,
	expandRemainder,
	expandScreenMedia,
	indexManifest,
	itemCoverEntries,
	ItemIndex,
	mediaFileEntries,
} from './expand'
import { DownloadCache, DownloadQueue, entriesFromUrls, QueueProgress, QueueResult } from './queue'
import { manifestVersions, staleItems, urlsToEvict, VersionedManifest } from './registry'

export const PROJECT_KEY = 'project-cache-set'

/** Bumped when the walk learns to fetch more, so rows an earlier build wrote are walked again on launch. */
export const DOWNLOAD_FORMAT = 2

export const collectionKey = (collectionId: number): string => `collection:${collectionId}`

export interface DownloaderDeps extends ExpandContext {
	/** What to call this download in a storage list. */
	title?: string
	cache: DownloadCache
	fetch: (url: string) => Promise<Response>
	workerReady: () => Promise<void>
	rows: DownloadRows
	onProgress?: (progress: QueueProgress) => void
	concurrency?: number
}

/** The registry reads and writes the downloader needs, so the tests can drive it without Dexie. */
export interface DownloadRows {
	all: () => Promise<DexieDownload[]>
	get: (key: string) => Promise<DexieDownload | undefined>
	put: (row: DexieDownload) => Promise<void>
	remove: (key: string) => Promise<void>
}

/**
 * What a fetched document adds to the queue. A stop's media queues right behind the stop, which is the order
 * a visitor walking behind the downloader needs.
 */
const expanderFor =
	(index: ItemIndex, context: ExpandContext) =>
	async (entry: DownloadEntry, response: Response): Promise<DownloadEntry[]> => {
		if (entry.type === 'collections') {
			return expandCollectionDocument(entry.id, (await response.json()) as GetCollectionsShowResponse, index, context)
		}

		if (entry.type === 'collection_items') {
			const items = ((await response.json()) as GetCollectionItemsIndexResponse).data ?? []
			// Stops before their thumbnails: a visitor walking behind the downloader needs the next stop first.
			return [...expandItems(items, index, context), ...(await itemCoverEntries(items, context))]
		}

		if (entry.type === 'screens') {
			return expandScreenMedia((await response.json()) as GetScreensShowResponse, index, context)
		}

		if (entry.type === 'media_items') {
			return mediaFileEntries((await response.json()) as MediaItem, context)
		}

		return []
	}

/**
 * What a run starts from. The walk supplies the order — a stop, then the media that stop names — and the
 * manifest supplies completeness: it already lists every media item the server gathered, section media
 * included, so a section shape the walk does not yet read costs a tour late media rather than no media.
 */
const seedEntries = (collectionIds: number[], index: ItemIndex, deps: DownloaderDeps): DownloadEntry[] => {
	const walked = collectionIds.flatMap(id => expandCollection(id, [], index, deps))

	return [...walked, ...expandRemainder(index, deps, walked)]
}

const runQueue = (entries: DownloadEntry[], index: ItemIndex, deps: DownloaderDeps): DownloadQueue<DownloadEntry> =>
	new DownloadQueue(entries, {
		cache: deps.cache,
		fetch: deps.fetch,
		workerReady: deps.workerReady,
		expand: expanderFor(index, deps),
		onProgress: deps.onProgress,
		concurrency: deps.concurrency,
	})

const rowFrom = (
	key: string,
	manifest: VersionedManifest,
	deps: DownloaderDeps,
	result: QueueResult,
): DexieDownload => ({
	key,
	title: deps.title,
	language: deps.language,
	format: DOWNLOAD_FORMAT,
	versions: manifestVersions(manifest),
	urls: result.urls,
	bytes: result.bytes,
	state: result.state,
	updatedAt: Date.now(),
})

export interface Download {
	queue: DownloadQueue<DownloadEntry>
	/**
	 * Move these stops to the front of what is left. Their own URLs, not their media's: a stop's media
	 * queues immediately behind the stop as its document lands, so moving the stop moves its media too.
	 */
	prioritizeStops: (stopIds: number[]) => void
	/** Resolves once the run has finished and the registry row records what landed. */
	done: Promise<DownloadState>
}

const stopPrioritizer =
	(index: ItemIndex, deps: DownloaderDeps, queue: DownloadQueue<DownloadEntry>) =>
	(stopIds: number[]): void => {
		const { api, accountId, language } = deps

		const urls = stopIds.flatMap(id => {
			const screen = index.screens?.get(id)
			if (!screen) return []

			return [api.resourceUrls(screen.accountId ?? accountId, 'screens', id, screen.version, language)[0]]
		})

		queue.prioritize(urls)
	}

/**
 * Download one tour, from the collection's own published subtree. The manifest names every item beneath
 * it, so a project that has no offline set configured still offers a working per-tour download.
 */
export const downloadCollection = async (collectionId: number, deps: DownloaderDeps): Promise<Download> => {
	const manifest = await deps.api.fetchCollectionManifest(deps.accountId, collectionId)
	const index = indexManifest(manifest)
	const queue = runQueue(seedEntries([collectionId], index, deps), index, deps)

	return {
		queue,
		prioritizeStops: stopPrioritizer(index, deps, queue),
		done: record(collectionKey(collectionId), manifest, deps, queue),
	}
}

/**
 * Download the project's offline set: every item a CSM flagged through PublishingCache. `package` and
 * `download` mean the same thing on the web, which has no binary to ship content inside.
 */
export const downloadProjectCacheSet = async (deps: DownloaderDeps): Promise<Download> => {
	const full = await deps.api.fetchManifestFull(deps.accountId)
	const cached = cachedOnly(full)
	const index = indexManifest(cached)
	const collections = [...(index.collections?.keys() ?? [])]

	const queue = runQueue(seedEntries(collections, index, deps), index, deps)

	return {
		queue,
		prioritizeStops: stopPrioritizer(index, deps, queue),
		done: record(PROJECT_KEY, cached, deps, queue),
	}
}

const record = async (
	key: string,
	manifest: VersionedManifest,
	deps: DownloaderDeps,
	queue: DownloadQueue<DownloadEntry>,
): Promise<DownloadState> => {
	// The row exists before the first byte lands, so a browser killed mid-download leaves something to
	// resume from rather than nothing.
	await deps.rows.put(rowFrom(key, manifest, deps, { state: 'downloading', urls: [], bytes: 0, failed: [] }))

	const result = await queue.run()
	await deps.rows.put(rowFrom(key, manifest, deps, result))

	return result.state
}

/** Only the items a `PublishingCache` row put in the offline set, whichever cache type it named. */
const cachedOnly = (full: ManifestFull): VersionedManifest => {
	const kept: VersionedManifest = {}

	for (const [section, items] of Object.entries(full) as [keyof ManifestFull, ManifestFullItem[]][]) {
		const flagged = items?.filter(item => item.cached)
		if (flagged?.length) kept[section as keyof VersionedManifest] = flagged
	}

	return kept
}

/** Re-run a row from the URLs it recorded. Every URL already cached is a free hit, so this costs the gaps. */
export const retryDownload = async (key: string, deps: DownloaderDeps): Promise<DownloadState | undefined> => {
	const row = await deps.rows.get(key)
	if (!row) return undefined

	const queue = new DownloadQueue(entriesFromUrls(row.urls), {
		cache: deps.cache,
		fetch: deps.fetch,
		workerReady: deps.workerReady,
		concurrency: deps.concurrency,
	})

	const result = await queue.run()
	await deps.rows.put({ ...row, urls: result.urls, bytes: result.bytes, state: result.state, updatedAt: Date.now() })

	return result.state
}

/** Forget a download, taking with it only the content no other row still claims. */
export const removeDownload = async (key: string, deps: DownloaderDeps): Promise<string[]> => {
	const evicted = urlsToEvict(key, await deps.rows.all())

	await Promise.all(evicted.map(url => deps.cache.delete(url).catch(() => false)))
	await deps.rows.remove(key)

	return evicted
}

/**
 * Bring every downloaded tour up to date, silently: a prompt most visitors dismiss does not fix stale content.
 * A failed row is retried from its URLs; one an earlier build wrote never recorded the media, so it is re-walked.
 */
export const refreshDownloads = async (deps: DownloaderDeps): Promise<void> => {
	const rows = await deps.rows.all()

	for (const row of rows) {
		if (row.state === 'downloading') continue

		if (row.format === DOWNLOAD_FORMAT && (row.state === 'incomplete' || row.state === 'error')) {
			await retryDownload(row.key, deps)
			continue
		}

		await refreshRow(row, deps)
	}
}

const refreshRow = async (row: DexieDownload, deps: DownloaderDeps): Promise<void> => {
	const manifest = await manifestForRow(row, deps)
	if (!manifest) return

	const { changed, removed } = staleItems(row, manifest)
	const stale = row.format !== DOWNLOAD_FORMAT || changed.length > 0 || removed.length > 0
	// A row built for another language is not stale, it is a different download; the gate reopens instead.
	if (row.language !== deps.language || !stale) return

	const collectionId = collectionIdFrom(row.key)
	const index = indexManifest(manifest)
	const collections = collectionId === undefined ? [...(index.collections?.keys() ?? [])] : [collectionId]

	await record(row.key, manifest, deps, runQueue(seedEntries(collections, index, deps), index, deps))
}

const manifestForRow = async (row: DexieDownload, deps: DownloaderDeps): Promise<VersionedManifest | undefined> => {
	const collectionId = collectionIdFrom(row.key)

	try {
		if (collectionId === undefined) return cachedOnly(await deps.api.fetchManifestFull(deps.accountId))
		return (await deps.api.fetchCollectionManifest(deps.accountId, collectionId)) as Manifest
	} catch {
		// No connection, or the tour is no longer published. Either way the row stays as it is.
		return undefined
	}
}

const collectionIdFrom = (key: string): number | undefined => {
	const [prefix, id] = key.split(':')

	return prefix === 'collection' ? Number(id) : undefined
}
